/**
 * Sofort sichtbare Ablage (User 01.11.2026: „Die Datei sollte sofort
 * dastehen (wie Google Drive) und dann richtig hochgeladen werden“).
 *
 * Beim Drop — Dateien, Outlook-Mails, Postfach-Mails, Mail-Anhänge — steht je
 * Eintrag SOFORT ein Platzhalter in der Zielliste (Name, Typ, „wird
 * hochgeladen …“). Ist der echte Eintrag geladen, verschwindet der
 * Platzhalter im selben Zug; schlägt es fehl, bleibt er rot stehen mit
 * „Erneut versuchen“ und „Entfernen“.
 *
 * Reiner Zustand ohne DOM — die Übergänge sind hier geprüft
 * (`ablagePlatzhalter.test.ts`), der Hook `useAblagePlatzhalter` hält ihn.
 */

export type PlatzhalterStatus = "laeuft" | "fertig" | "fehler";

export interface AblagePlatzhalter {
    id: string;
    name: string;
    mimeType?: string | null;
    status: PlatzhalterStatus;
    /** Zeile unter dem Namen, z. B. „Mail wird samt Anhängen abgelegt …“. */
    text?: string;
    fehler?: string;
}

export interface NeuerPlatzhalter {
    name: string;
    mimeType?: string | null;
    text?: string;
}

let zaehler = 0;
const neueId = () => `ablage-${Date.now().toString(36)}-${(zaehler += 1)}`;

export function hinzufuegen(
    liste: readonly AblagePlatzhalter[],
    neue: readonly NeuerPlatzhalter[],
): { liste: AblagePlatzhalter[]; ids: string[] } {
    const dazu = neue.map((n) => ({ id: neueId(), name: n.name || "Datei", mimeType: n.mimeType ?? null, text: n.text, status: "laeuft" as const }));
    return { liste: [...dazu, ...liste], ids: dazu.map((d) => d.id) };
}

const setze = (liste: readonly AblagePlatzhalter[], ids: readonly string[], patch: Partial<AblagePlatzhalter>) =>
    liste.map((p) => (ids.includes(p.id) ? { ...p, ...patch } : p));

/** Hochgeladen — wartet nur noch darauf, dass der echte Eintrag geladen ist. */
export const alsFertig = (liste: readonly AblagePlatzhalter[], ids: readonly string[]) =>
    setze(liste, ids, { status: "fertig", fehler: undefined });

export const alsFehler = (liste: readonly AblagePlatzhalter[], id: string, fehler: string) =>
    setze(liste, [id], { status: "fehler", fehler });

export const alsLaufend = (liste: readonly AblagePlatzhalter[], id: string, text?: string) =>
    setze(liste, [id], { status: "laeuft", fehler: undefined, ...(text ? { text } : {}) });

export const entfernen = (liste: readonly AblagePlatzhalter[], ids: readonly string[]) =>
    liste.filter((p) => !ids.includes(p.id));

/** Die echte Liste ist neu geladen: Fertige räumen, Laufende und Fehler bleiben. */
export const fertigeRaeumen = (liste: readonly AblagePlatzhalter[]) => liste.filter((p) => p.status !== "fertig");

export function statusText(p: AblagePlatzhalter): string {
    if (p.status === "fehler") return p.fehler || "Fehlgeschlagen";
    if (p.status === "fertig") return "Hochgeladen – wird angezeigt …";
    return p.text || "Wird hochgeladen …";
}

/** Grund eines gescheiterten Uploads für den Platzhalter — die deutsche Meldung des Servers. */
export function uploadFehlerText(err: unknown): string {
    // Nicht lesbar (gesperrt, noch offen): die Meldung sagt schon alles.
    if (err && typeof err === "object" && (err as { grund?: unknown }).grund === "nicht-lesbar") {
        return (err as Error).message;
    }
    const grund = err instanceof Error && err.message ? err.message : "";
    return grund ? `Nicht hochgeladen – ${grund}` : "Nicht hochgeladen.";
}
